import { useEffect, useMemo, useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import Switch from "@/components/Switch";
import { toFormat } from "@/lib/dateHelpers";
import type { UpcomingMemberElectionDateNode } from "../upcoming-dates/types";

export type UpdateElectionDateDialogResult = { action: "assign"; electionDateId: string } | { action: "remove" };

export type UpdateElectionDateDialogProps = {
  open: boolean;
  selectedCount: number;
  upcomingDates: UpcomingMemberElectionDateNode[];
  saving?: boolean;
  onClose(): void;
  onConfirm(result: UpdateElectionDateDialogResult): void;
};

export default function UpdateElectionDateDialog({
  open,
  selectedCount,
  upcomingDates,
  saving = false,
  onClose,
  onConfirm
}: UpdateElectionDateDialogProps) {
  const [selectedDateId, setSelectedDateId] = useState("");
  const [removeDate, setRemoveDate] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedDateId("");
      setRemoveDate(false);
    }
  }, [open]);

  const dateOptions = useMemo(
    () =>
      [...upcomingDates]
        .sort((a, b) => a.electionDate.localeCompare(b.electionDate))
        .map((date) => ({
          value: date.id,
          label: toFormat(date.electionDate, "MMM dd, yyyy") ?? date.electionDate
        })),
    [upcomingDates]
  );

  const hasUpcomingDates = dateOptions.length > 0;
  const canSubmit = selectedCount > 0 && (removeDate || !!selectedDateId) && !saving;

  const handleRemoveChange = () => {
    setRemoveDate((current) => {
      const next = !current;
      if (next) {
        setSelectedDateId("");
      }
      return next;
    });
  };

  const handleSelectDate = (value: string) => {
    setSelectedDateId(value);
    setRemoveDate(false);
  };

  const handleConfirm = () => {
    if (!canSubmit) return;

    if (removeDate) {
      onConfirm({ action: "remove" });
      return;
    }

    onConfirm({ action: "assign", electionDateId: selectedDateId });
  };

  return (
    <Dialog
      open={open}
      headline="Update Election Date"
      onClosed={onClose}
      hasFormElements
      displayOverflow
      disableClose={saving}
      className="md:w-[480px]"
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={saving} testId="update-election-date-cancel">
            Cancel
          </Button>
          <Button variant="filled" onClick={handleConfirm} disabled={!canSubmit} testId="update-election-date-confirm">
            Update Date
          </Button>
        </>
      }
    >
      <div className="min-w-[320px] space-y-6">
        <p className="text-body-medium-semibold" data-testid="update-election-date-count">
          Total People to Update Election Date: <strong>{selectedCount}</strong>
        </p>
        <p className="text-body-medium">
          Choose a new upcoming election date for the selected people, or remove their current date to postpone their
          election.
        </p>
        <Select
          label="Election Date"
          placeholder="Select Upcoming Date"
          options={dateOptions}
          value={selectedDateId}
          onSelect={handleSelectDate}
          disabled={removeDate || !hasUpcomingDates}
          supportingText={hasUpcomingDates ? undefined : "No upcoming election dates are available."}
          data-testid="update-election-date-select"
        />
        <Switch
          headline="Remove Election Date"
          supportingText="The selected people will remain eligible for election without an assigned date until a new date is selected."
          selected={removeDate}
          onChange={handleRemoveChange}
        />
      </div>
    </Dialog>
  );
}
